import React from "react";
import { Card, Row, Col, Typography, Divider, Button } from "antd";
import { useNavigate } from "react-router-dom";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent"; // Private route wrapper
import "./PrivacyPolicy.css";

const { Title, Paragraph, Text, Link } = Typography;

const sections = [
  {
    title: "1. Introduction",
    content: (
      <>
        <Paragraph>
          WeHelpApp (“the App”) is developed and operated by Devantis B.V.,
          gevestigd te Den Haag, Nederland.
        </Paragraph>
        <Paragraph>
          Devantis B.V. (“we”, “us”, or “our”) respects your privacy and is
          committed to protecting your personal data in accordance with the
          General Data Protection Regulation (EU) 2016/679 (“GDPR”), Dutch
          privacy laws, and applicable international regulations.
        </Paragraph>
        <Paragraph>
          This Privacy Policy explains how we collect, use, store, share, and
          protect your personal data when you use WeHelpApp.
        </Paragraph>
      </>
    ),
  },
  {
    title: "2. Data Controller",
    content: (
      <>
        <Paragraph><Text strong>Devantis B.V.</Text></Paragraph>
        <Paragraph>The Hague, The Netherlands</Paragraph>
        <Paragraph>Email: <Link href="mailto:privacy@wehelpapp.com">privacy@wehelpapp.com</Link></Paragraph>
        <Paragraph>Devantis B.V. is the data controller as defined under the GDPR.</Paragraph>
      </>
    ),
  },
  {
    title: "3. Personal Data We Collect",
    content: (
      <>
        <Title level={4}>3.1 Data You Provide</Title>
        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Profile information (optional)</li>
          <li>Messages and communication within the App</li>
          <li>Support requests and correspondence</li>
        </ul>

        <Title level={4}>3.2 Automatically Collected Data</Title>
        <ul>
          <li>Device type, operating system, app version</li>
          <li>IP address (anonymized where possible)</li>
          <li>Usage data (interactions, session duration, logs)</li>
          <li>Crash reports and performance diagnostics</li>
        </ul>

        <Title level={4}>3.3 Location Data</Title>
        <ul>
          <li>Approximate location (city or region level only, if enabled)</li>
          <li>Exact location only if explicitly required and with user consent</li>
        </ul>
        <Paragraph>We do not collect precise GPS location by default.</Paragraph>
      </>
    ),
  },
  {
    title: "4. Purpose of Processing",
    content: (
      <ul>
        <li>To create and manage user accounts</li>
        <li>To connect users within the App</li>
        <li>To facilitate communication between users</li>
        <li>To ensure platform safety and fraud prevention</li>
        <li>To improve app functionality and user experience</li>
        <li>To comply with legal obligations</li>
        <li>To provide customer support</li>
      </ul>
    ),
  },
  {
    title: "5. Legal Basis (GDPR Article 6)",
    content: (
      <>
        <ul>
          <li>Performance of a contract</li>
          <li>Legitimate interest</li>
          <li>Consent</li>
          <li>Legal obligation</li>
        </ul>
        <Paragraph>You may withdraw consent at any time.</Paragraph>
      </>
    ),
  },
  {
    title: "6. Data Retention",
    content: (
      <>
        <ul>
          <li>Account data: retained while the account is active</li>
          <li>Deleted accounts: deleted or anonymized within a reasonable period</li>
          <li>Legal/security data: retained as required by law</li>
        </ul>
        <Paragraph>
          Deletion requests: <Link href="mailto:privacy@wehelpapp.com">privacy@wehelpapp.com</Link>
        </Paragraph>
      </>
    ),
  },
  {
    title: "7. Data Sharing & Third Parties",
    content: (
      <>
        <Paragraph>We do not sell personal data.</Paragraph>
        <ul>
          <li>Hosting and cloud providers</li>
          <li>Analytics and crash-reporting tools</li>
          <li>Customer support systems</li>
          <li>Payment or identity verification services (if applicable)</li>
        </ul>
      </>
    ),
  },
  {
    title: "8. International Data Transfers",
    content: (
      <ul>
        <li>EU Standard Contractual Clauses (SCCs)</li>
        <li>EU adequacy decisions</li>
      </ul>
    ),
  },
  {
    title: "9. Security Measures",
    content: (
      <ul>
        <li>Encryption in transit and at rest</li>
        <li>Secure authentication</li>
        <li>Access control and least-privilege</li>
        <li>Regular security reviews</li>
      </ul>
    ),
  },
  {
    title: "10. Your Rights Under GDPR",
    content: (
      <ul>
        <li>Access your personal data</li>
        <li>Correction of inaccurate data</li>
        <li>Deletion (“right to be forgotten”)</li>
        <li>Restriction or objection</li>
        <li>Data portability</li>
        <li>Withdraw consent</li>
        <li>File a complaint with the Dutch DPA</li>
      </ul>
    ),
  },
  {
    title: "11. Children’s Privacy",
    content: (
      <Paragraph>
        WeHelpApp is not intended for children under 16. We do not knowingly
        collect personal data from minors.
      </Paragraph>
    ),
  },
  {
    title: "12. Cookies & Tracking Technologies",
    content: (
      <ul>
        <li>App functionality</li>
        <li>Security</li>
        <li>Performance analytics</li>
      </ul>
    ),
  },
  {
    title: "13. Changes to This Privacy Policy",
    content: (
      <Paragraph>
        We may update this policy from time to time. The latest version is
        always available within the App.
      </Paragraph>
    ),
  },
  {
    title: "14. Contact Information",
    content: (
      <>
        <Paragraph>Email: <Link href="mailto:privacy@wehelpapp.com">privacy@wehelpapp.com</Link></Paragraph>
        <Paragraph>Devantis B.V., The Hague, The Netherlands</Paragraph>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Split sections into two arrays for left/right
  const leftSections = sections.filter((_, i) => i % 2 === 0);
  const rightSections = sections.filter((_, i) => i % 2 !== 0);

  return (
 
      <div className="mr-top mt-5 p-5 ant-card" style={{ maxWidth: "100%" }}>
        <Card style={{ width: "100%", padding: "24px" }}>
          {/* Back Button */}
          {/* <div style={{ marginBottom: 24 }}>
            <Button type="primary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div> */}

          <Title level={2} className="text-center mb-4">
            Privacy Policy – WeHelpApp
          </Title>
          <Text className="effective-date">Effective date: January 2026</Text>
          <Divider />

          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              {leftSections.map((section, index) => (
                <section key={index}>
                  <Title level={3}>{section.title}</Title>
                  {section.content}
                  <Divider />
                </section>
              ))}
            </Col>

            <Col xs={24} md={12}>
              {rightSections.map((section, index) => (
                <section key={index}>
                  <Title level={3}>{section.title}</Title>
                  {section.content}
                  <Divider />
                </section>
              ))}
            </Col>
          </Row>
        </Card>
      </div>
   
  );
};

export default PrivacyPolicy;
